import {
  Component,
  Input,
  OnChanges,
  OnInit,
  OnDestroy,
  SimpleChanges,
  Output,
  EventEmitter,
  AfterViewInit,
  ChangeDetectorRef,
} from "@angular/core";
import {
  GroupedMaterialWithCustomers,
  BusinessPartnerWithOrders,
} from "../interfaces/material-group-partner";
import { PageEvent } from "@angular/material/paginator";
import { CommonModule } from "@angular/common";
import { MatDialog } from "@angular/material/dialog";
import {
  ExportDialogComponent,
  ExportFileFormat,
  ExportRange,
} from "app/layout/export-overlay";
import { ClientMaterialExport } from "./client-material-detail-export.service";
import { MatSnackBar } from "@angular/material/snack-bar";
import { UnitMeasureDisplayComponent } from "app/shared/components/unit-measure-display/unit-measure-display.component";
import { BehaviorSubject, Subject, takeUntil } from "rxjs";
import { FuseNavigationService } from '@fuse/components/navigation/navigation.service';
import { FuseVerticalNavigationComponent } from '@fuse/components/navigation/vertical/vertical.component';
import { SmartGridComponent } from "app/shared/components/smart-grid/components/smart-grid/smart-grid.component";
import { ColDef } from "app/shared/components/smart-grid/core/interfaces/col-def.interface";
import { GridConfig } from "app/shared/components/smart-grid/core/interfaces/grid-config.interface";
import { PageChangedEvent } from "app/shared/components/smart-grid/core/interfaces/pagination-state.interface";
import { SortChangedEvent } from "app/shared/components/smart-grid/core/interfaces/sort-state.interface";
import { RowExpansionChangedEvent } from "app/shared/components/smart-grid/core/interfaces/grid-events.interface";

// Cell Renderers
import { MaterialCodeNameCellRendererComponent } from "../cell-renderers/material-code-name-cell-renderer.component";
import { MaterialTotalQuantityCellRendererComponent } from "../cell-renderers/material-total-quantity-cell-renderer.component";
import { CustomerCountCellRendererComponent } from "../cell-renderers/customer-count-cell-renderer.component";
import { QuantityUMPCellRendererComponent } from "../cell-renderers/quantity-ump-cell-renderer.component";
import { MaterialActionsCellRendererComponent } from "../cell-renderers/material-actions-cell-renderer.component";
import { CustomerDetailsCellRendererComponent } from "../cell-renderers/customer-details-cell-renderer.component";
import { MatButtonModule } from "@angular/material/button";
import { MatIconModule } from "@angular/material/icon";
import { MatTooltipModule } from "@angular/material/tooltip";

// Interface para los eventos de paginación
export interface ClientMaterialPageEvent {
  pageIndex: number;
  pageSize: number;
  sortActive?: string;
  sortDirection?: string;
}

export interface InitEventMonitorClientMaterialSummary {
  emitEvent: boolean;
}

// Interface para las opciones de exportación
export interface ExportOptions {
  format: ExportFileFormat;
  range: ExportRange;
}

// Constantes para el título del modal
const CLIENT_MATERIAL_TITLE = "Exportar Materiales de Cliente";

@Component({
  selector: "app-monitor-client-material-summary",
  standalone: true,
  imports: [
    CommonModule,
    SmartGridComponent,
    MatButtonModule,
    MatTooltipModule,
    UnitMeasureDisplayComponent,
    MatIconModule,
    MatTooltipModule,
  ],
  templateUrl: "./monitor-client-material-summary.component.html",
  styleUrl: "./monitor-client-material-summary.component.scss",
  providers: [ClientMaterialExport],
})
export class MonitorClientMaterialSummaryComponent
  implements OnInit, OnChanges, AfterViewInit, OnDestroy
{
  // Navigation state for conditional styling
  isNavOpened: boolean = true;
  private _unsubscribeAll: Subject<any> = new Subject<any>();

  // Inputs del componente padre
  @Input() clientMaterials: any[] = [];
  @Input() groupedMaterialsWithCustomers: GroupedMaterialWithCustomers[] = [];
  @Input() UMPActive: boolean = false;
  @Input() totalItems: number = 0;
  @Input() isLoading: boolean = false;
  @Input() pageSize: number = 100;
  @Input() pageIndex: number = 0;
  @Input() tripId!: number;
  @Input() documentTypes: string[] = [];

  // Outputs para comunicar eventos al padre
  @Output() pageChanged = new EventEmitter<ClientMaterialPageEvent>();
  @Output() initPage =
    new EventEmitter<InitEventMonitorClientMaterialSummary>();

  // BehaviorSubject para UMPActive (reactivo)
  private UMPActive$ = new BehaviorSubject<boolean>(false);

  // Column definitions
  materialColumnDefs: ColDef<GroupedMaterialWithCustomers>[] = [];
  customerColumnDefs: ColDef<BusinessPartnerWithOrders>[] = [];

  // Grid configurations
  materialGridConfig: GridConfig<GroupedMaterialWithCustomers> = {};
  customerGridConfig: GridConfig<BusinessPartnerWithOrders> = {};

  // Data para grid expandido
  expandedCustomerData: BusinessPartnerWithOrders[] = [];

  constructor(
    private clientMaterialExport: ClientMaterialExport,
    private cdRef: ChangeDetectorRef,
    private dialog: MatDialog,
    private snackBar: MatSnackBar,
    private readonly _fuseNavigationService: FuseNavigationService
  ) {}

  ngOnInit() {
    this.initializeMaterialColumnDefs();
    this.initializeCustomerColumnDefs();
    this.initializeMaterialGridConfig();
    this.initializeCustomerGridConfig();
    this.emitLoadDataEvent();
    this.setupNavigationListener();
  }

  private setupNavigationListener(): void {
    const navigation = this._fuseNavigationService.getComponent<FuseVerticalNavigationComponent>('mainNavigation');
    if (navigation) {
      this.isNavOpened = navigation.opened;
      navigation.openedChanged
        .pipe(takeUntil(this._unsubscribeAll))
        .subscribe((opened: boolean) => {
          this.isNavOpened = opened;
          this.cdRef.detectChanges();
        });
    }
  }

  ngOnDestroy(): void {
    this._unsubscribeAll.next(null);
    this._unsubscribeAll.complete();
  }

  ngOnChanges(changes: SimpleChanges) {
    if (changes["UMPActive"]) {
      this.UMPActive$.next(this.UMPActive);
    }
  }

  ngAfterViewInit() {
    this.cdRef.detectChanges();
  }

  /**
   * Initialize column definitions for the main materials grid
   */
  private initializeMaterialColumnDefs(): void {
    this.materialColumnDefs = [
      // 1. Material Column
      {
        colId: "material",
        headerName: "Material",
        width: 250,
        minWidth: 200,
        sortable: true,
        filterable: true,
        cellRenderer: MaterialCodeNameCellRendererComponent,
        field: "code" as any,
      },

      // 2. Customer Count Column
      {
        colId: "customerCount",
        headerName: "Cantidad de Clientes",
        width: 180,
        minWidth: 150,
        sortable: true,
        filterable: true,
        cellRenderer: CustomerCountCellRendererComponent,
        field: "totalOrderCount" as any,
      },

      // 3. Total Column (UMB/UMP)
      {
        colId: "total",
        headerName: "Total",
        width: 220,
        minWidth: 200,
        sortable: true,
        filterable: true,
        cellRenderer: MaterialTotalQuantityCellRendererComponent,
        cellRendererParams: {
          UMPActive$: this.UMPActive$,
        },
        field: "totalBaseQuantity" as any,
        cellClass: "text-right",
        headerClass: "text-right",
      },

      // 4. Actions Column
      {
        colId: "actions",
        headerName: "",
        width: 80,
        minWidth: 80,
        maxWidth: 80,
        sortable: false,
        filterable: false,
        cellRenderer: MaterialActionsCellRendererComponent,
        cellRendererParams: {
          onExport: (material: GroupedMaterialWithCustomers) =>
            this.exportSingleMaterialToExcel(material),
        },
        cellClass: "text-center",
      },
    ];
  }

  /**
   * Initialize column definitions for the nested customer details grid
   */
  private initializeCustomerColumnDefs(): void {
    this.customerColumnDefs = [
      // 1. Customer Column
      {
        colId: "customer",
        headerName: "Cliente",
        width: 250,
        minWidth: 200,
        sortable: true,
        filterable: true,
        cellRenderer: CustomerDetailsCellRendererComponent,
        field: "shipToName" as any,
      },

      // 2. Quantity Column (UMB/UMP)
      {
        colId: "quantity",
        headerName: "Cantidad",
        width: 220,
        minWidth: 200,
        sortable: true,
        filterable: true,
        cellRenderer: QuantityUMPCellRendererComponent,
        cellRendererParams: {
          UMPActive$: this.UMPActive$,
        },
        field: "baseQuantity" as any,
        cellClass: "text-right",
        headerClass: "text-right",
      },
    ];
  }

  /**
   * Initialize grid configuration for materials grid
   */
  private initializeMaterialGridConfig(): void {
    this.materialGridConfig = {
      pagination: {
        enabled: true,
        mode: "api", // Server-side pagination
        pageSize: this.pageSize,
        pageSizeOptions: [10, 50, 100, 150],
      },
      sorting: {
        enabled: true,
        mode: "local", // Client-side sorting
        multiColumn: false,
      },
      columnFilters: {
        enabled: true,
        mode: "local",
      },
      rowExpansion: {
        enabled: true,
        multipleExpand: false, // Only one row expanded at a time
        expandOnClick: false,
      },
      styling: {
        maxHeight: "70vh",
        cellPaddingY: '0px'
      },
      settingsPanel: {
        enableColumnVisibility: false,
        enabled: false
      }
    };
  }

  /**
   * Initialize grid configuration for customer details grid
   */
  private initializeCustomerGridConfig(): void {
    this.customerGridConfig = {
      pagination: {
        enabled: false, // No pagination for customer details
      },
      sorting: {
        enabled: true,
        mode: "local", // Local sorting
      },
      columnFilters: {
        enabled: true,
        mode: "local",
      },
      settingsPanel: {
        enableColumnVisibility: false,
        enabled: false
      },
      styling: {
        maxHeight: "70vh",
        cellPaddingY: '0px'
      }
    };
  }

  /**
   * Emite el evento para cargar datos con los parámetros actuales
   */
  private emitLoadDataEvent() {
    this.initPage.emit({
      emitEvent: true,
    });
  }

  /**
   * Handle page change event from smart-grid
   */
  onMaterialPageChanged(event: PageChangedEvent) {
    const pageEvent: ClientMaterialPageEvent = {
      pageIndex: event.pageIndex,
      pageSize: event.pageSize,
      // Sorting is handled separately by onMaterialSortChanged
    };

    this.pageChanged.emit(pageEvent);
  }

  /**
   * Handle sort change event from smart-grid
   */
  onMaterialSortChanged(event: SortChangedEvent<GroupedMaterialWithCustomers>) {
    // Get first sort state (we only have single column sorting)
    const sortState = event.sortStates[0];

    const sortEvent: ClientMaterialPageEvent = {
      pageIndex: 0,
      pageSize: this.pageSize,
      sortActive: sortState?.field as string,
      sortDirection: sortState?.direction,
    };

    this.pageChanged.emit(sortEvent);
  }

  /**
   * Handle row expansion event from smart-grid
   */
  onMaterialRowExpanded(
    event: RowExpansionChangedEvent<GroupedMaterialWithCustomers>
  ) {
    if (event.expanded) {
      // ✨ SOLUCIÓN: Enriquecer businessPartners con UOM del material padre
      // El cell renderer genérico espera que cada fila tenga baseUnitOfMeasure y pararellUnitOfMeasure
      // Como el API no los incluye en los businessPartners, los copiamos del material padre
      this.expandedCustomerData = (event.data.businessPartners || []).map(bp => ({
        ...bp,
        baseUnitOfMeasure: event.data.baseUnitOfMeasure,
        pararellUnitOfMeasure: event.data.pararellUnitOfMeasure
      }));
    } else {
      this.expandedCustomerData = [];
    }
  }

  /**
   * Maneja el evento de presionar el botón de exportar todos los clientes
   * Ahora abre el modal de exportación
   */
  onExportButtonPressed() {
    if (!this.clientMaterialExport.canStartTask()) return;

    // Validar que tenemos los parámetros necesarios
    if (!this.tripId) {
      this.snackBar.open("tripId is required for export", "Cerrar", {
        duration: 5000,
      });
      return;
    }

    const dialogRef = this.dialog.open(ExportDialogComponent, {
      data: { title: CLIENT_MATERIAL_TITLE },
    });

    dialogRef.afterClosed().subscribe((options: ExportOptions) => {
      if (options) {
        this.clientMaterialExport.export(
          options.format,
          options.range,
          this.totalItems,
          this.pageSize,
          this.pageIndex,
          this.tripId,
          this.documentTypes
        );
      }
    });
  }

  /**
   * Exporta un material específico usando el modal de exportación
   */
  exportSingleMaterialToExcel(material: GroupedMaterialWithCustomers) {
    if (!this.clientMaterialExport.canStartTask()) return;

    const dialogRef = this.dialog.open(ExportDialogComponent, {
      data: { title: `Exportar ${material.name || material.code}` },
    });

    dialogRef.afterClosed().subscribe((options: ExportOptions) => {
      if (options) {
        // Convert material back to client format for export compatibility
        const clientForExport = this.convertMaterialToClientFormat(material);
        this.clientMaterialExport.exportSingleClient(
          options.format,
          clientForExport
        );
      }
    });
  }

  /**
   * Converts a GroupedMaterialWithCustomers to ClientMaterialSummary format for export
   */
  private convertMaterialToClientFormat(
    material: GroupedMaterialWithCustomers
  ): any {
    return {
      id: material.materialId,
      orderCount: material.totalOrderCount,
      clientName: material.name,
      clientCode: material.code,
      shipTo: {
        _id: material.materialId,
        externalKeys: { code: material.code },
        name: material.name,
      },
      soldTo: {
        _id: material.materialId,
        externalKeys: { code: material.code },
        name: material.name,
      },
      materialsTotalPrice: material.totalSoldQuantity,
      materials: material.businessPartners.map((partner) => ({
        materialCode: material.code,
        materialName: material.name,
        totalBaseQuantity: partner.baseQuantity,
        baseUnitOfMeasureCode: material.baseUnitOfMeasure.code,
        baseUnitOfMeasureName: material.baseUnitOfMeasure.name,
        totalParallelQuantity: partner.pararellQuantity,
        parallelUnitOfMeasureCode: material.pararellUnitOfMeasure.code,
        parallelUnitOfMeasureName: material.pararellUnitOfMeasure.name,
        materialsTotalPrice: partner.soldQuantity,
      })),
    };
  }

  /**
   * Método para refrescar datos (llamado externamente si es necesario)
   */
  refreshData() {
    this.emitLoadDataEvent();
  }

  /**
   * Método para resetear paginación
   */
  resetPagination() {
    this.emitLoadDataEvent();
  }
}
